import type { FieldMetaData } from "./fieldsMetaData";
import { createFieldMetaData } from "./createFieldMetaData";
import moment from "moment";
import { getAppointmentPhase, getGroundTransportType, getPickupLocationType } from "../enumutils";

export interface GooglePlaceState {
  googlePlaceId: number;
  placeIdentifier: string;
  name: string;
  address: string;
  geo: server.dto.Geo;
}

export interface GroundTransportDateTimeState {
  dateTime: moment.Moment
  departArrive: server.dto.DepartArrive
  inTheNightOf: boolean
}

export interface GroundTransportFormState {
  groundTransportId: number;

  artist: server.dto.ArtistSmall
  phase: server.dto.AppointmentPhase
  travellers: server.dto.ContactSmallV2[]
  type: server.dto.GroundTransportType
  notes: string
  driver: server.dto.Driver

  startPickupLocationType: server.dto.PickupLocationType;
  startPickupLocationOther: string;

  endPickupLocationType: server.dto.PickupLocationType;
  endPickupLocationOther: string;

  appointmentDateTime: server.dto.AppointmentDateTime;
  date: GroundTransportDateTimeState
  durationInMinutes: number

  startPlace: GooglePlaceState
  endPlace: GooglePlaceState

  directions: server.dto.GoogleDirections
}

export const mapGroundTransportToState = (
    groundTransport: server.dto.GroundTransport,
    travellers: server.dto.ContactSmallV2[],
    serverReference: server.dto.ServerReference,
): GroundTransportFormState => ({
  groundTransportId: groundTransport.groundTransportId,
  artist: groundTransport.artist,
  phase: getAppointmentPhase(groundTransport.phase, serverReference),
  travellers: travellers,
  // @ts-expect-error .
  type: groundTransport.groundTransportType === "unknown" ? null : getGroundTransportType(groundTransport.groundTransportType, serverReference),
  notes: groundTransport.notes,
  driver: groundTransport.driver,

  startPickupLocationType: getPickupLocationType(groundTransport.startPickupLocationType, serverReference),
  startPickupLocationOther: groundTransport.startPickupLocationOther,
  endPickupLocationType: getPickupLocationType(groundTransport.endPickupLocationType, serverReference),
  endPickupLocationOther: groundTransport.endPickupLocationOther,

  appointmentDateTime: groundTransport.dateTime,
  date: {
    departArrive: groundTransport.departOrArrive,
    dateTime: groundTransport.departOrArrive === "departAt"
      ? moment.utc(groundTransport.dateTime.startDisplay)
      : moment.utc(groundTransport.dateTime.endDisplay),
    inTheNightOf: groundTransport.dateTime.mode === "hasTimeStartsInNight",
  },

  durationInMinutes: groundTransport.durationInMinutes,

  startPlace: {
    googlePlaceId: 0, // This will be updated in the future to work like the train
    placeIdentifier: groundTransport.startGooglePlaceIdentifier,
    name: groundTransport.startGooglePlaceName,
    address: groundTransport.startGooglePlaceAddress,
    geo: groundTransport.startGooglePlaceGeo,
  },
  endPlace: {
    googlePlaceId: 0, // This will be updated in the future to work like the train
    placeIdentifier: groundTransport.endGooglePlaceIdentifier,
    name: groundTransport.endGooglePlaceName,
    address: groundTransport.endGooglePlaceAddress,
    geo: groundTransport.endGooglePlaceGeo,
  },
  directions: groundTransport.directions,
});

export const getUpdateGroundTransportInstruction = (gt: GroundTransportFormState): server.dto.UpdateGroundTransportInstruction => ({
    artistId: gt.artist.artistId,
    dateTime: gt.date.dateTime.toDate(),
    departOrArrive: gt.date.departArrive,
    driver: {
      name: gt.driver.name,
      mobile: gt.driver.mobile,
      email: gt.driver.email,
      vehicle: gt.driver.vehicle,
    },
    durationInMinutes: gt.durationInMinutes,
    endGooglePlaceAddress: gt.endPlace.address,
    endGooglePlaceGeo: gt.endPlace.geo,
    endGooglePlaceIdentifier: gt.endPlace.placeIdentifier,
    endGooglePlaceName: gt.endPlace.name,
    endPickupLocation: {
      value: gt.endPickupLocationType.value,
      other: gt.endPickupLocationOther,
    },
    groundTransportId: gt.groundTransportId,
    groundTransportType: gt.type === null ? "unknown" : gt.type.value,
    inTheNightOf: gt.date.inTheNightOf,
    notes: gt.notes,
    phase: gt.phase.value,

    startGooglePlaceAddress: gt.startPlace.address,
    startGooglePlaceGeo: gt.startPlace.geo,
    startGooglePlaceIdentifier: gt.startPlace.placeIdentifier,
    startGooglePlaceName: gt.startPlace.name,
    startPickupLocation: {
      value: gt.startPickupLocationType.value,
      other: gt.startPickupLocationOther,
    },
    passengers: gt.travellers.map(c => c.contactId),
  });

export interface GroundFieldMetaData {
    artist: FieldMetaData;
    travellers: FieldMetaData;
    phase: FieldMetaData;
    groundTransportType: FieldMetaData;
    notes: FieldMetaData;
    distanceInMeters: FieldMetaData;
    durationInMinutes: FieldMetaData;

    departureDate: FieldMetaData;
    departureType: FieldMetaData;
    departurePlace: FieldMetaData;

    arrivalDate: FieldMetaData;
    arrivalType: FieldMetaData;
    arrivalPlace: FieldMetaData;

    driverName: FieldMetaData;
    driverMobile: FieldMetaData;
    driverEmail: FieldMetaData;
    driverVehicle: FieldMetaData;
}

export const getGroundFieldMetaData = (
    environment: server.dto.SelectedEnvironment,
    item: GroundTransportFormState,
    itemPermissions: server.dto.ObjectPermissions
): GroundFieldMetaData => ({
    artist: createFieldMetaData(
        !item.artist,
        !item.artist && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    travellers: createFieldMetaData(
        !item.travellers || item.travellers.length === 0,
        (!item.travellers || item.travellers.length === 0) && !itemPermissions.canModify,
        !itemPermissions.canModify,
    ),

    phase: createFieldMetaData(
        false, //Phase is never empty
        false, //Phase is never hidden
        !itemPermissions.canModify,
    ),

    groundTransportType: createFieldMetaData(
        !item.type,
        !item.type && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    notes: createFieldMetaData(
        !item.notes,
        !item.notes && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    distanceInMeters: createFieldMetaData(
        !item.directions?.distanceInMeters,
        !item.directions?.distanceInMeters && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    durationInMinutes: createFieldMetaData(
        (!item.durationInMinutes && !item.directions?.durationEstimateInMinutes),
        (!item.durationInMinutes && !item.directions?.durationEstimateInMinutes) && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    departureDate: createFieldMetaData(
        false,
        false, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    departureType: createFieldMetaData(
        false,
        false,
        !itemPermissions.canModify,
    ),

    departurePlace: createFieldMetaData(
        !item.startPlace || !item.startPlace.placeIdentifier,
        (!item.startPlace || !item.startPlace.placeIdentifier) && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),  

    arrivalDate: createFieldMetaData(
        !item.durationInMinutes,
        !item.durationInMinutes && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),  

    arrivalType: createFieldMetaData(
        false,
        false, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    arrivalPlace: createFieldMetaData(
        !item.endPlace || !item.endPlace.placeIdentifier,
        (!item.endPlace || !item.endPlace.placeIdentifier) && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),  

    driverName: createFieldMetaData(
        !item.driver?.name,
        !item.driver?.name && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    driverMobile: createFieldMetaData(
        !item.driver?.mobile,
        !item.driver?.mobile && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    driverEmail: createFieldMetaData(
        !item.driver?.email,
        !item.driver?.email && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    driverVehicle: createFieldMetaData(
        !item.driver?.vehicle,
        !item.driver?.vehicle && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),
});